/**
 * ConversationMgmtTour-specific types and step definitions.
 *
 * The ConversationMgmtTour walks users through conversation management —
 * shared phone number visibility, open/close lifecycle, unread filtering,
 * and the Assigned to Me / Unassigned views.
 */

import type { TourStep } from '../types';

/** Unique identifiers for each ConversationMgmtTour step */
export type ConversationMgmtTourStepId =
  | 'conv_mgmt_welcome'
  | 'highlight_all_tab'
  | 'explain_shared_number'
  | 'highlight_status_toggle'
  | 'close_conversation'
  | 'highlight_closed_tab'
  | 'explain_reopen'
  | 'highlight_unread_btn'
  | 'highlight_assigned_tab'
  | 'highlight_unassigned_tab'
  | 'conv_mgmt_complete';

/** Ordered list of all ConversationMgmtTour steps */
export const CONV_MGMT_TOUR_STEPS: TourStep[] = [
  {
    id: 'conv_mgmt_welcome',
    type: 'modal',
    title: '',
    content: '',
  },
  {
    id: 'highlight_all_tab',
    type: 'highlight',
    targetSelector: '[data-tour-id="inbox-all"]',
    title: 'All Conversations',
    content:
      'Welcome to your conversation inbox. Every conversation across your entire agency lives here. Your agency shares one phone number — to your clients it looks like a single line of communication, but on your side you have full visibility into who on your team sent each message.',
    placement: 'right',
  },
  {
    id: 'explain_shared_number',
    type: 'highlight',
    targetSelector: '[data-tour-id="conversation-chat-area"]',
    title: 'One Number, Full Transparency',
    content:
      'Your agency shares a single phone number, but every outbound message shows the sender\'s profile avatar next to it. Your clients see one consistent number while your team can always see exactly who sent each message.',
    positionInside: true,
  },
  {
    id: 'highlight_status_toggle',
    type: 'highlight',
    targetSelector: '[data-tour-id="status-toggle"]',
    title: 'Open & Close Conversations',
    content:
      'This toggle controls whether a conversation is "Open" or "Closed." Close a conversation when you\'ve finished helping the client — it keeps your inbox focused on active work. Now click this toggle to close the conversation.',
    placement: 'bottom',
    allowInteraction: true,
    advanceOnElement: '[data-tour-id="close-conversation-modal"]',
  },
  {
    id: 'close_conversation',
    type: 'highlight',
    targetSelector: '[data-tour-id="close-conversation-modal"]',
    title: 'Confirm Close',
    content:
      'Confirm to close this conversation. Don\'t worry — closing is not permanent. The conversation and all its messages are preserved and accessible anytime.',
    placement: 'top',
    allowInteraction: true,
    advanceOnElementRemoved: '[data-tour-id="close-conversation-modal"]',
  },
  {
    id: 'highlight_closed_tab',
    type: 'highlight',
    targetSelector: '[data-tour-id="open-closed-toggle"]',
    title: 'View Closed Conversations',
    content:
      'Click the "Closed" side of this toggle to see all your closed conversations. They\'re always accessible — nothing is lost. When you\'re done looking, click Next to continue.',
    placement: 'right',
    allowInteraction: true,
  },
  {
    id: 'explain_reopen',
    type: 'highlight',
    targetSelector: '[data-tour-id="open-closed-toggle"]',
    title: 'Re-opening Conversations',
    content:
      'Closed conversations re-open automatically when the client sends you a new message or when you send them one. You can also manually re-open any conversation by clicking the toggle back to "Open" in the conversation header.',
    placement: 'right',
  },
  {
    id: 'highlight_unread_btn',
    type: 'highlight',
    targetSelector: '[data-tour-id="unread-btn"]',
    title: 'Unread Filter',
    content:
      'Click the "Unread" button to filter your inbox to only show conversations with new, unread messages. This is a quick way to see what needs your attention without scrolling through your entire list. Click Next when you\'re ready to continue.',
    placement: 'left',
    allowInteraction: true,
  },
  {
    id: 'highlight_assigned_tab',
    type: 'highlight',
    targetSelector: '[data-tour-id="inbox-assigned"]',
    title: 'Assigned to Me',
    content:
      'The "Assigned to Me" tab shows only conversations that have been assigned to you. Use this as your personal workspace to stay focused on the conversations you\'re responsible for without distractions from the full inbox.',
    placement: 'right',
  },
  {
    id: 'highlight_unassigned_tab',
    type: 'highlight',
    targetSelector: '[data-tour-id="inbox-unassigned"]',
    title: 'Unassigned Conversations',
    content:
      'The "Unassigned" tab shows conversations that haven\'t been assigned to anyone on your team yet. Check this regularly to make sure no client inquiry or lead falls through the cracks.',
    placement: 'right',
  },
  {
    id: 'conv_mgmt_complete',
    type: 'modal',
    title: 'Conversation Management Tour Complete',
    content:
      'You now know how to manage conversations — from understanding the shared phone number, to opening and closing conversations, filtering by unread status, and navigating your personal and unassigned views.',
  },
];
